import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { isStripeTestMode } from "@/config/stripePlans";
import { PENDING_STRIPE_ACCOUNT_KEY } from "@/components/admin_dashboard/StripePaymentsRow";

// Stripe sends owners back here from Connect onboarding: ?status=success when
// they finish, ?status=refresh when the setup link expired or was reopened.
const IntegrationResult = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const statusParam = searchParams.get("status");
  const { businessId } = useAuth();

  const [verifying, setVerifying] = useState(true);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const verifyStripeConnection = async () => {
      const pendingId = localStorage.getItem(PENDING_STRIPE_ACCOUNT_KEY);

      try {
        if (!pendingId) {
          // Nothing pending in this browser (already verified, or set up
          // elsewhere): the saved connection is the answer.
          if (!businessId) {
            setSuccess(false);
            return;
          }
          const { data } = await supabase
            .from("businesses")
            .select("stripe_connect_id")
            .eq("business_id", businessId)
            .maybeSingle();
          setSuccess(statusParam === "success" && !!data?.stripe_connect_id);
          return;
        }

        if (statusParam !== "success") {
          setSuccess(false);
          return;
        }

        const { data, error } = await supabase.functions.invoke("stripe-connect-verify", {
          body: { accountId: pendingId, test_mode: isStripeTestMode },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);

        if (data?.verified) {
          localStorage.removeItem(PENDING_STRIPE_ACCOUNT_KEY);
          setSuccess(true);
        } else {
          setSuccess(false);
        }
      } catch (err: any) {
        toast.error(err?.message || "Couldn't check your Stripe connection");
        setSuccess(false);
      } finally {
        setVerifying(false);
      }
    };

    verifyStripeConnection();
  }, [statusParam, businessId]);

  const handleGoToDashboard = () => {
    navigate("/?tab=settings&settings=connectors");
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-lg text-center">
          <CardHeader className="pt-10 pb-6 flex flex-col items-center">
            <Loader2 className="w-10 h-10 text-muted-foreground animate-spin mb-6" />
            <CardTitle className="text-2xl font-heading font-semibold text-foreground">
              Checking your Stripe account
            </CardTitle>
            <CardDescription className="text-sm mt-2">
              This takes a few seconds.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-lg text-center">
        <CardHeader className="pt-10 pb-6">
          {success ? (
            <div className="mx-auto bg-primary/10 w-20 h-20 rounded-full flex items-center justify-center mb-6">
              <CheckCircle2 className="w-10 h-10 text-primary" />
            </div>
          ) : (
            <div className="mx-auto bg-destructive/10 w-20 h-20 rounded-full flex items-center justify-center mb-6">
              <XCircle className="w-10 h-10 text-destructive" />
            </div>
          )}

          <CardTitle className="text-2xl font-heading font-semibold text-foreground">
            {success ? "Stripe is connected" : "Stripe setup isn't finished"}
          </CardTitle>
          <CardDescription className="text-sm mt-2">
            {success
              ? "Customers can now pay online when they book."
              : "Stripe still needs a few details before you can take online payments."}
          </CardDescription>
        </CardHeader>
        <CardContent className="px-8 pb-6 text-sm text-muted-foreground leading-relaxed">
          {success
            ? "Payouts go to the bank account you added in Stripe."
            : "You can carry on from Settings → Connectors, under Dibs Bookings."}
        </CardContent>
        <CardFooter className="pb-10 pt-2 flex justify-center">
          <Button size="sm" className="standard-action-button" onClick={handleGoToDashboard}>
            Back to Connectors
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
};

export default IntegrationResult;
